import { filterCompanyTable } from 'api/services/CompanyServiceNew';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Company } from 'types/Companies';
import { getLanguages, updateUserProfile } from 'api/services/UserService';
import {
  Departments,
  LanguageLevelsOptions,
  salutations,
  Segments,
  translate,
  translateReverse,
  userProductInterests,
  valueToOption
} from 'api/types/ReferenceData';
import { UserContext } from 'context/User';
import { countries } from 'helpers/countries';
import { toEntityUserProfile } from 'helpers/utilsTS';
import { User } from 'types/User';
import { formatPhoneNumber } from 'components/pages/register/Register/helper';
import { loadUserProfile } from 'api/services/UserServiceTS';

export const UserProfileModel = () => {
  const methods = useForm();

  const user = useContext(UserContext);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [languages, setLanguages] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newsletter, setNewsletter] = useState(false);

  const setUserData = (userData: any, lang?: any) => {
    const data: User = userData;
    const defaultData: User = {
      key: '',
      email: '',
      firstname: '',
      lastname: '',
      phone_number: '',
      phone_country_code: '',
      salutation: '',
      department: '',
      country_code: '',
      segments: [],
      interests: [''],
      newsletter: false,
      bio: '',
      roles: [],
      job_title: '',
      media: [{ type: 'linkedin', url: '' }],
      shipping_address: {
        personOfContact: '',
        person_of_contact: '',
        company: '',
        city: '',
        street: '',
        street_additional: '',
        postal_code: '',
        // Watch out, in this file we have a bigger risk for bugs related to the User type!
        // The API data type and the form state type are combined and not always strictly
        // checked. `valueToOption` returning `any` and the logic in `toEntityUserProfile` with many
        // ignore-comments should be reviewed during the next bigger refactoring in this area!!
        // @ts-ignore
        country: { value: '', label: '' },
        country_code: ''
      }
    };

    if (data.newsletter) {
      setNewsletter(data?.newsletter);
    }
    const initialData: User = {
      ...defaultData,
      ...data,
      department: valueToOption(data?.department, Departments),
      country_code: valueToOption(data?.country_code, countries),
      salutation: valueToOption(data?.salutation, salutations),
      segments: data?.segments?.map(segment =>
        valueToOption(segment, Segments)
      ),

      languages: data?.languages?.map(
        (language: { name: string; level: string }) => ({
          name: {
            value: translateReverse(language.name, lang || languages),
            label: language.name
          },
          level: {
            value: language.level,
            label: translate(language.level, LanguageLevelsOptions)
          }
        })
      ),
      interests: data?.interests?.map(interest =>
        valueToOption(interest, userProductInterests)
      ),
      // @ts-ignore
      shipping_address: {
        ...data.shipping_address,
        company: data?.shipping_address?.company
          ? data.shipping_address?.company
          : '',
        // @ts-ignore see comment above!
        country: data?.shipping_address?.country_code
          ? valueToOption(data?.shipping_address?.country_code, countries)
          : data?.country_code
          ? valueToOption(data?.country_code, countries)
          : { value: '', label: '' },
        person_of_contact:
          // @ts-ignore
          data.shipping_address?.person_of_contact ||
          data.firstname + ' ' + data.lastname,
        // @ts-ignore
        country_code: {
          value: data.shipping_address?.country_code || '',
          // @ts-ignore
          label:
            valueToOption(data?.shipping_address?.country_code, countries) || ''
        }
      }
    };
    Object.keys(initialData).forEach((key: any) => {
      // @ts-ignore

      if (initialData[key] === undefined) {
        // @ts-ignore

        initialData[key] = defaultData[key];
      }
    });

    initialData.phone_number = initialData.phone_number
      .replace('(', '')
      .replace(')', '');

    methods.reset({
      ...initialData
    });
    setLoading(false);
  };

  useEffect(() => {
    filterCompanyTable(
      '',
      {
        page: 0,
        size: 20,
        sort: []
      },
      (res: any) => setCompanies(res.data.content),
      () => toast.error("Couldn't load companies")
    );
    getLanguages(null, (res: any) => {
      const lang = res.data.content.map((language: any) => ({
        label: language.name,
        value: language.key
      }));
      setLanguages(
        res.data.content.map((language: any) => ({
          label: language.name,
          value: language.key
        }))
      );
      loadUserProfile(res => {
        setUserData(res.data, lang);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values: any) => {
    setIsSaving(true);
    values.phone_number = formatPhoneNumber(values.phone_number);
    updateUserProfile(
      toEntityUserProfile(values),
      (res: any) => {
        setIsSaving(false);
        setUserData(res.data);
        const { userDataFromRequest } = res.data;
        user?.updateUser?.({
          ...user,
          ...userDataFromRequest
        });
      },
      () => setIsSaving(false)
    );
  };

  return {
    loading,
    companies,
    isSaving,
    languages,
    newsletter,
    setNewsletter,
    methods,
    onSubmit
  };
};
